// src/pages/HelpPage.js
//
// Public help page — visible without login. The route is registered in App.js and
// never gated on `isAuthenticated`, so anyone evaluating the product can read it.
//
// If you want to update screenshots, drop them into:
//     public/help/setup.png
//     public/help/manage.png
//     public/help/scan.png
//     public/help/profile.png
// They're referenced via process.env.PUBLIC_URL so they resolve correctly under the
// /admin basename. Missing files just collapse (onError hides the <img>).
import React from 'react';
import { Link } from 'react-router-dom';
import '../styles/HelpPage.css';

const Shot = ({ src, alt, caption }) => {
    const url = `${process.env.PUBLIC_URL}/help/${src}`;
    return (
        <figure className="help-shot">
            <img
                src={url}
                alt={alt}
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
            {caption && <figcaption>{caption}</figcaption>}
        </figure>
    );
};

const HelpPage = () => {
    return (
        <div className="help-page">
            <h1>ZappyRewards Help</h1>
            <p className="help-subtitle">
                Everything you need to set up and run a loyalty program on ZappyRewards.
                You don't need a crypto background — the on-chain parts are handled for you.
            </p>

            <nav className="help-toc" aria-label="Table of contents">
                <strong>On this page</strong>
                <ol>
                    <li><a href="#getting-started">Getting started</a></li>
                    <li><a href="#off-vs-on-chain">Off-chain points vs. on-chain tokens</a></li>
                    <li><a href="#custodial">Custodial wallets, in plain English</a></li>
                    <li><a href="#features">Admin features (sidebar reference)</a></li>
                    <li><a href="#wallet-passes">Apple &amp; Google Wallet passes</a></li>
                    <li><a href="#subscription">Subscription, USDCa &amp; how to pay</a></li>
                    <li><a href="#glossary">Glossary</a></li>
                    <li><a href="#troubleshooting">Troubleshooting</a></li>
                </ol>
            </nav>

            {/* ====================================================================== */}
            <section id="getting-started" className="help-section">
                <h2>1. Getting started</h2>
                <p>
                    The flow from zero to a working program is short. Each step has its own
                    page in the sidebar.
                </p>
                <ol className="help-steps">
                    <li>
                        <strong>Create an account.</strong> Click <em>Login / Register</em>
                        in the top-right and sign up with your email (you'll receive a
                        confirmation link) or with Google.
                    </li>
                    <li>
                        <strong>Set up your first program.</strong> Go to
                        {' '}<em>Setup New Program</em>. Choose a name, short code, logo, and
                        decide between <em>off-chain points</em> or <em>on-chain tokens</em>
                        {' '}— see the next section if you're not sure.
                    </li>
                    <li>
                        <strong>Pick your active program.</strong> If you have more than one,
                        use <em>Select Program</em>. Everything below the sidebar's "Program"
                        section operates on the program you currently have selected.
                    </li>
                    <li>
                        <strong>Configure rewards.</strong> Open <em>Rewards Settings</em> and
                        define what participants can redeem (e.g. "Free coffee — 100 points").
                    </li>
                    <li>
                        <strong>Enroll participants.</strong> Participants register themselves
                        on the user app (the customer-facing site at your program's link).
                        You can see them in <em>Participants</em> as they join.
                    </li>
                    <li>
                        <strong>Award and redeem.</strong> Use <em>Scan QR</em> at the counter:
                        scan a participant's wallet QR, then either award them points or let
                        them redeem a reward.
                    </li>
                </ol>
                <Shot src="setup.png" alt="Setup Program screen" caption="Setup New Program: asset type, name, logo." />
            </section>

            {/* ====================================================================== */}
            <section id="off-vs-on-chain" className="help-section">
                <h2>2. Off-chain points vs. on-chain tokens</h2>
                <p>
                    When you create a program you pick how points are stored. The choice is
                    permanent for that program, so it's worth understanding.
                </p>

                <div className="help-compare">
                    <div className="help-compare-col">
                        <h3>Off-chain points</h3>
                        <ul>
                            <li>Points live in our database as a number on the participant.</li>
                            <li>Fast, free, no blockchain involved.</li>
                            <li>Participants don't need a wallet.</li>
                            <li>You can't "send" points outside the program — they only exist here.</li>
                        </ul>
                        <p className="help-when">
                            <strong>Pick this if:</strong> you just want a digital punch card
                            and don't care about ownership/portability.
                        </p>
                    </div>
                    <div className="help-compare-col">
                        <h3>On-chain tokens (Algorand ASA)</h3>
                        <ul>
                            <li>Points are real Algorand Standard Assets (ASA) on the Algorand
                                blockchain.</li>
                            <li>Each participant has a wallet that actually holds the tokens.</li>
                            <li>Balances are verifiable on a public explorer.</li>
                            <li>Tokens can — if you choose — be made transferable outside the program.</li>
                        </ul>
                        <p className="help-when">
                            <strong>Pick this if:</strong> you want loyalty points your members
                            truly own, or you want a Web3-native brand story.
                        </p>
                    </div>
                </div>

                <p className="help-callout">
                    <strong>Don't worry about the blockchain bits.</strong> ZappyRewards handles
                    wallet creation, funding, opt-ins, and signing on your behalf. The next
                    section explains exactly what's happening behind the scenes.
                </p>
            </section>

            {/* ====================================================================== */}
            <section id="custodial" className="help-section">
                <h2>3. Custodial wallets, in plain English</h2>
                <p>
                    On Algorand, every wallet that holds a token has to do three things first:
                    exist (with a minimum balance of ALGO), opt in to that specific token, and
                    have its private key around to sign transactions. For non-crypto-native
                    participants, ZappyRewards does all of this automatically. We call this a
                    <strong> custodial wallet</strong>.
                </p>

                <h3>What happens when a participant joins an on-chain program</h3>
                <ol className="help-steps">
                    <li>
                        The participant signs up with email or Google (no crypto knowledge required).
                    </li>
                    <li>
                        ZappyRewards generates an Algorand wallet for them, stores the encrypted
                        private key on our servers, and funds it with a small amount of ALGO from
                        the program treasury (currently 0.25 ALGO — enough for the minimum balance
                        plus the opt-in fee).
                    </li>
                    <li>
                        The wallet opts in to your program's ASA. From this point on, the
                        participant can receive tokens.
                    </li>
                    <li>
                        When you award or redeem tokens via Scan QR, we sign the transaction
                        on the participant's behalf and broadcast it to the network.
                    </li>
                </ol>

                <h3>"Take ownership" (optional, advanced)</h3>
                <p>
                    A participant who knows what they're doing can move their tokens out of the
                    custodial wallet by linking their own Algorand wallet (e.g. Pera). They keep
                    full custody and can transfer freely. This is intentionally a one-way door
                    and is currently disabled in the user app while we finish the flow.
                </p>

                <h3>Funding</h3>
                <p>
                    The program treasury covers participant wallet funding and ASA transfers.
                    See <Link to="/subscription" className="help-link">Subscription</Link> for
                    treasury top-ups and pricing.
                </p>
            </section>

            {/* ====================================================================== */}
            <section id="features" className="help-section">
                <h2>4. Admin features (sidebar reference)</h2>

                <h3>Dashboard</h3>
                <p>
                    Landing page after login. At-a-glance counts of programs, participants, and
                    recent activity.
                </p>

                <h3>Setup New Program</h3>
                <p>
                    Create a new loyalty program. You choose:
                </p>
                <ul>
                    <li><strong>Name</strong> — shown on wallet passes and the user app.</li>
                    <li><strong>Short name</strong> — used in URLs and the pass filename.</li>
                    <li><strong>Logo</strong> — required for Apple and Google Wallet passes.</li>
                    <li><strong>Asset type</strong> — off-chain points or on-chain ASA.</li>
                    <li><strong>ASA settings</strong> (on-chain only) — token name, unit, decimals.</li>
                </ul>

                <h3>Select Program</h3>
                <p>
                    If you run multiple programs, switch between them here. The selected program
                    drives every program-scoped page below it.
                </p>

                <h3>Manage Program</h3>
                <p>
                    Edit the selected program's name, logo, and metadata. You can't change the
                    asset type after creation (off-chain ↔ on-chain is locked in to keep
                    balances consistent).
                </p>
                <Shot src="manage.png" alt="Manage Program screen" caption="Manage Program: edit name, logo, and metadata." />

                <h3>Rewards Settings</h3>
                <p>
                    Define the catalog of things participants can redeem points/tokens for. Each
                    reward has a name, description, and a cost. When a participant redeems at
                    the counter, the cost is deducted from their balance.
                </p>

                <h3>Participants</h3>
                <p>
                    List, search, and manage the people enrolled in the selected program. Each
                    row shows their balance, email/username, custodial wallet (if on-chain),
                    and join date.
                </p>

                <h3>Scan QR</h3>
                <p>
                    Counter tool. Scan a participant's QR (from their Apple/Google Wallet pass
                    or the user app), then:
                </p>
                <ul>
                    <li><strong>Award</strong> — give them points/tokens for a purchase.</li>
                    <li><strong>Redeem</strong> — let them spend balance on a reward you defined.</li>
                </ul>
                <Shot src="scan.png" alt="Scan QR screen" caption="Scan QR: award or redeem at the counter." />

                <h3>Analytics</h3>
                <p>
                    Activity charts for the selected program — sign-ups over time, points
                    awarded, redemptions, active participants.
                </p>

                <h3>Profile</h3>
                <p>
                    Your admin account. Email/username, the option to link a personal Pera
                    wallet for receiving payouts, and quick access to your subscription. You
                    sign a small (zero-fee, never broadcast) Algorand transaction to prove you
                    own the wallet you're linking.
                </p>
                <Shot src="profile.png" alt="Profile screen" caption="Profile: account info and Pera wallet linking." />

                <h3>Subscription</h3>
                <p>
                    Tier, status, expiry, and payment. See <a href="#subscription">section 6</a>{' '}
                    for tiers and how to pay in USDCa.
                </p>
            </section>

            {/* ====================================================================== */}
            <section id="wallet-passes" className="help-section">
                <h2>5. Apple &amp; Google Wallet passes</h2>
                <p>
                    Participants can save a digital loyalty card to Apple Wallet or Google
                    Wallet directly from the user app. The card shows:
                </p>
                <ul>
                    <li>Program name and logo.</li>
                    <li>The participant's member name (their username or email).</li>
                    <li>Current balance — for on-chain programs this is fetched live from
                        Algorand and displayed with the token's unit (e.g. "25 GRAMO").</li>
                    <li>A QR code that, for on-chain programs, encodes the participant's
                        Algorand address (so the same QR can be scanned by any Algorand wallet
                        app to send them tokens).</li>
                </ul>
                <p>
                    Balances stay in sync: when you award or redeem at the counter, the pass
                    updates on the participant's phone within a few seconds via Apple's APNs
                    push and Google's Wallet update API.
                </p>
            </section>

            {/* ====================================================================== */}
            <section id="subscription" className="help-section">
                <h2>6. Subscription, USDCa &amp; how to pay</h2>

                <h3>Tiers</h3>
                <div className="help-tiers">
                    <div className="help-tier">
                        <h4>Starter</h4>
                        <ul>
                            <li>Up to a set number of participants per program.</li>
                            <li>All admin features included.</li>
                            <li>Renews annually.</li>
                        </ul>
                    </div>
                    <div className="help-tier">
                        <h4>Pro</h4>
                        <ul>
                            <li>Higher participant cap.</li>
                            <li>All admin features included.</li>
                            <li>Renews annually.</li>
                        </ul>
                    </div>
                </div>
                <p className="help-muted">
                    For exact participant caps and prices, see the{' '}
                    <Link to="/subscription" className="help-link">Subscription page</Link>{' '}
                    when logged in — they're updated in one place.
                </p>

                <h3>Subscription status</h3>
                <ul>
                    <li><strong>Active</strong> — full access, paid up.</li>
                    <li><strong>Demo</strong> — 20-day trial that lets you exercise the full
                        on-chain flow. Tokens awarded during demo are frozen at expiry until
                        you pay.</li>
                    <li><strong>Read-only</strong> — subscription expired. You can still log
                        in and inspect data, but can't award/redeem or create programs.</li>
                    <li><strong>Expired</strong> — fully lapsed.</li>
                </ul>

                <h3>What is USDCa?</h3>
                <p>
                    <strong>USDCa</strong> is USDC (the US-dollar-pegged stablecoin) issued as
                    an Algorand Standard Asset on the Algorand blockchain. 1 USDCa is intended
                    to always equal 1 USDC on Ethereum or any other supported chain — it's the
                    same dollar, just travelling on Algorand's rails. We accept USDCa because
                    Algorand transactions settle in seconds and cost a fraction of a cent in
                    fees.
                </p>

                <h3>How to pay your subscription in USDCa</h3>
                <p>You need three things: a wallet, some USDCa, and ZappyRewards' payment address.</p>
                <ol className="help-steps">
                    <li>
                        <strong>Install an Algorand wallet.</strong> The most common choice is
                        {' '}<a href="https://perawallet.app/" target="_blank" rel="noreferrer" className="help-link">Pera Wallet</a>
                        {' '}(iOS, Android, browser extension). Other options:
                        {' '}<a href="https://www.defly.app/" target="_blank" rel="noreferrer" className="help-link">Defly</a>,
                        {' '}<a href="https://exodus.com/" target="_blank" rel="noreferrer" className="help-link">Exodus</a>.
                    </li>
                    <li>
                        <strong>Fund it with ALGO.</strong> You need a tiny amount of ALGO
                        (less than $1) to cover network fees and the wallet's minimum balance.
                        You can buy ALGO on most major exchanges (Coinbase, Binance, Kraken)
                        and withdraw it to your wallet's Algorand address.
                    </li>
                    <li>
                        <strong>Opt in to USDCa.</strong> Inside the wallet, find "Add asset"
                        and search for <em>USDC</em> (Asset ID <code>31566704</code> on Algorand
                        mainnet). Confirming the opt-in costs about 0.001 ALGO and reserves a
                        slot in your wallet for the token.
                    </li>
                    <li>
                        <strong>Get USDCa.</strong> Two paths:
                        <ul>
                            <li><em>Buy on an exchange that supports USDC on Algorand</em> (e.g.
                                Coinbase lets you withdraw USDC directly on Algorand — make sure
                                you pick "Algorand" as the network, not Ethereum, or you'll lose
                                the funds).</li>
                            <li><em>Swap from ALGO inside your wallet</em> using a built-in
                                DEX integration (Pera, Defly).</li>
                        </ul>
                    </li>
                    <li>
                        <strong>Pay from the Subscription page.</strong> Go to{' '}
                        <Link to="/subscription" className="help-link">Subscription</Link>,
                        pick a tier, and follow the on-screen instructions. You'll be shown a
                        payment address and an exact USDCa amount, plus a QR code you can scan
                        from your wallet to pre-fill the transaction.
                    </li>
                </ol>

                <p className="help-callout">
                    <strong>Always send USDCa on the Algorand network</strong>, not Ethereum
                    or any other chain. The address formats look different (Algorand addresses
                    are 58 characters, no <code>0x</code> prefix) — if your wallet refuses the
                    address, you've picked the wrong network.
                </p>
            </section>

            {/* ====================================================================== */}
            <section id="glossary" className="help-section">
                <h2>7. Glossary</h2>
                <dl className="help-glossary">
                    <dt>ALGO</dt>
                    <dd>The native currency of the Algorand blockchain. Used for network fees
                        and the wallet minimum balance. Not the same thing as your loyalty
                        tokens.</dd>

                    <dt>Algorand</dt>
                    <dd>A fast, low-fee public blockchain. Transactions settle in seconds for
                        a fraction of a cent.</dd>

                    <dt>ASA (Algorand Standard Asset)</dt>
                    <dd>A token issued on Algorand. Your on-chain loyalty points are an ASA.
                        USDC on Algorand is also an ASA (called USDCa).</dd>

                    <dt>Custodial wallet</dt>
                    <dd>A wallet whose private key is held on the participant's behalf by
                        ZappyRewards. Lets non-crypto users participate without managing keys.</dd>

                    <dt>Decimals (ASA)</dt>
                    <dd>How many fractional digits a token supports. A token with 2 decimals
                        and a raw balance of 2500 displays as "25.00".</dd>

                    <dt>Minimum balance</dt>
                    <dd>Algorand requires every wallet to hold at least 0.1 ALGO, plus 0.1
                        ALGO for each ASA the wallet has opted into. Custodial wallets are
                        pre-funded above this floor.</dd>

                    <dt>Opt-in</dt>
                    <dd>A small transaction a wallet must send before it can receive a given
                        ASA. Done automatically for custodial wallets.</dd>

                    <dt>Pera Wallet</dt>
                    <dd>Popular Algorand wallet, available as mobile app and browser extension.
                        Used by admins to link a wallet to their profile and by advanced
                        participants who want self-custody.</dd>

                    <dt>USDC</dt>
                    <dd>A stablecoin pegged 1:1 to the US dollar, issued by Circle.</dd>

                    <dt>USDCa</dt>
                    <dd>USDC issued on the Algorand network, as an ASA. Same dollar, different
                        rails. This is what ZappyRewards subscriptions are paid in.</dd>

                    <dt>Wallet pass</dt>
                    <dd>The digital loyalty card a participant saves to Apple Wallet or Google
                        Wallet. Updates in near-real-time when balances change.</dd>
                </dl>
            </section>

            {/* ====================================================================== */}
            <section id="troubleshooting" className="help-section">
                <h2>8. Troubleshooting</h2>

                <h3>"Wallets are operating in different networks" when linking Pera</h3>
                <p>
                    Your Pera Wallet is connected to a different Algorand network (testnet vs.
                    mainnet) than the one ZappyRewards is configured for. Switch networks
                    inside Pera and try again.
                </p>

                <h3>The Apple Wallet pass shows the wrong balance</h3>
                <p>
                    Pass updates are pushed when you award/redeem. If the update didn't arrive,
                    delete the pass from Apple Wallet (long-press → trash) and re-download it
                    from the user app's "Add to Apple Wallet" button — that forces a fresh build.
                </p>

                <h3>The Google Wallet pass shows the wrong program name or logo</h3>
                <p>
                    Google caches loyalty class data the first time the pass is created. The
                    next "Save to Google Wallet" you trigger refreshes both the class and the
                    object. If it still looks stale, remove the pass from Google Wallet and
                    add it again.
                </p>

                <h3>Custodial wallet wasn't created for a new participant</h3>
                <p>
                    The treasury may be temporarily under-funded, or Algorand may have rejected
                    the funding transaction. We retry automatically. You can also force a retry
                    by reloading the participant's profile in the user app.
                </p>
            </section>

            <p className="help-footer-muted">
                Something missing or unclear? Tell us at{' '}
                <a href="mailto:support@zappyrewards.com" className="help-link">support@zappyrewards.com</a>
                {' '}and we'll fix this page.
            </p>
        </div>
    );
};

export default HelpPage;
